import { AuthProviderTenant } from '../AuthProviderTenant';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { chakraTheme } from '@duke-hq/ui/theme/chakraTheme';
import { ChakraProvider, Container } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink, httpLink, httpSubscriptionLink, splitLink } from '@trpc/client';
import SuperJSON from 'superjson';
import { createVersionCheckingLink } from '@duke-hq/ui/version/createVersionedTrpcClient';
import { unauthorisedRedirectLink } from '@duke-hq/libauth-client/unauthorisedRedirectLink';
import { ToastContainer, toast } from '@duke-hq/app-provider-web/components/ToastContainer';
import { t } from '@lingui/core/macro';
import { TenantRoutes } from '../TenantRoutes';
import { trpcTenant } from '../trpcTenant';
import { ErrorBoundary } from '@duke-hq/app-provider-web/components/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { messages } from '../../../locales/en';
import { CenteredSpinner } from '@duke-hq/app-provider-web/components/Loading';
import { NavButtonProvider } from '@duke-hq/ui/components/MainSidebar/NavButtonContext';
import { SubSidebarProvider } from '@duke-hq/ui/components/SubSidebar/SubSidebarContext';
import { WithFlags } from '../WithFlags';
import { WithNotifications } from '../WithNotifications';
import { WithReactQueryDevtools } from '../WithReactQueryDevtools';
import { TenantContextProvider } from '@duke-hq/app-provider-web/tenant/TenantContext';
import { FilesTrpcClientProvider } from '@duke-hq/svc-files-ui/FilesTrpcClientProvider';
import { getTenantContext } from '@duke-hq/libctx';

i18n.load('en', messages);
i18n.activate('en');

const url = '/api/provider';

function AppTenantWithFilesAuth() {
  const createToken = trpcTenant.auth.createFilesServiceToken.useMutation();
  const filesServiceUrl = getTenantContext()?.apis.FILES_API_ENDPOINT;

  const getAuthToken = useCallback(async () => {
    return await createToken.mutateAsync();
  }, [createToken]);

  return (
    <FilesTrpcClientProvider getAuthToken={getAuthToken} filesServiceUrl={filesServiceUrl}>
      <WithFlags>
        <WithNotifications>
          <WithReactQueryDevtools>
            <NavButtonProvider initialExpanded={false}>
              <SubSidebarProvider>
                <TenantRoutes />
              </SubSidebarProvider>
            </NavButtonProvider>
          </WithReactQueryDevtools>
        </WithNotifications>
      </WithFlags>
    </FilesTrpcClientProvider>
  );
}

export function AppTenant() {
  const queryClient = useMemo(() => new QueryClient(), []);
  const [trpcClient] = useState(() =>
    trpcTenant.createClient({
      links: [
        createVersionCheckingLink({
          clientVersion: process.env.APP_BUILD_HASH || '',
          onVersionMismatch: () => {
            toast({
              description: t`There is a new version of the app, please reload the page.`,
              status: 'info',
              variant: 'left-accent',
              isClosable: true,
              duration: null,
            });
          },
        }),
        unauthorisedRedirectLink({
          loginPath: '/login',
          publicPaths: ['/signup', '/magiclink', '/oidc/azure/callback', '/signout'],
        }),
        splitLink({
          condition(op) {
            // Route subscriptions to SSE link
            return op.type === 'subscription';
          },
          // when condition is true, use SSE for subscriptions
          true: httpSubscriptionLink({
            url,
            transformer: SuperJSON,
            eventSourceOptions: () => ({
              withCredentials: true,
            }),
          }),
          // when condition is false, use regular HTTP links
          false: splitLink({
            condition(op) {
              // Prevent any operation on the auth.* route from being sent
              // in a batch.
              if (op.path.startsWith('auth.')) {
                return true;
              }

              // check for context property `skipBatch`
              return Boolean(op.context.skipBatch);
            },
            // when condition is true, use normal request
            true: httpLink({
              url,
              transformer: SuperJSON,
            }),
            // when condition is false, use batching
            false: httpBatchLink({
              url,
              transformer: SuperJSON,
            }),
          }),
        }),
      ],
    }),
  );

  return (
    <TenantContextProvider>
      <I18nProvider i18n={i18n}>
        <HelmetProvider>
          <ChakraProvider theme={chakraTheme}>
            <ToastContainer />
            <trpcTenant.Provider client={trpcClient} queryClient={queryClient}>
              <QueryClientProvider client={queryClient}>
                <AuthProviderTenant>
                  <AppTenantWithFilesAuth />
                </AuthProviderTenant>
              </QueryClientProvider>
            </trpcTenant.Provider>
          </ChakraProvider>
        </HelmetProvider>
      </I18nProvider>
    </TenantContextProvider>
  );
}

/**
 * should contain all top level pages
 */
export function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Container py={{ base: '4', md: '8' }} px={{ base: '0', md: 8 }}>
        <Suspense fallback={<CenteredSpinner />}>{children}</Suspense>
      </Container>
    </ErrorBoundary>
  );
}
